import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GetAppIcon from '@mui/icons-material/GetApp'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import SearchIcon from '@mui/icons-material/Search'
import Fab from '@mui/material/Fab'
import Grow from '@mui/material/Grow'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { DigitalAsset } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useState } from 'react'
import { makeStyles } from 'tss-react/mui'

import Media from './Media'

const useStyles = makeStyles<void, 'root'>()((theme, _params, classes) => ({
  root: {
    boxShadow: `0px 0px 0px 1px ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  mediaRoot: {
    'borderTopLeftRadius': theme.shape.borderRadius,
    'borderTopRightRadius': theme.shape.borderRadius,
    'backgroundColor': theme.palette.grey[100],
    'position': 'relative',
    'height': '0',
    'paddingBottom': '56.25%',
    'overflow': 'hidden',
    '& img, & video': {
      position: 'absolute',
      // Using width/height vs top/left/bottom/right makes objectFit work.
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
  },
  warningIcon: {
    position: 'absolute',
    left: theme.spacing(2),
    bottom: theme.spacing(2),
    color: theme.palette.warning.light,
  },
  selected: {
    boxShadow: `0px 0px 0px 4px ${theme.palette.primary.light}`,
  },
  content: {
    padding: theme.spacing(2),
  },
  menuHoverRoot: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    display: 'none',
    [`.${classes.root}:hover &`]: {
      display: 'block',
    },
  },
  selectHoverRoot: {
    position: 'absolute',
    left: theme.spacing(2),
    top: theme.spacing(2),
    display: 'none',
    [`.${classes.root}:hover &`]: {
      display: 'block',
    },
  },
  selectHoverSelected: {
    display: 'block',
  },
  previewHoverRoot: {
    position: 'absolute',
    right: theme.spacing(2),
    bottom: theme.spacing(18),
    display: 'none',
    [`.${classes.root}:hover &`]: {
      display: 'block',
    },
  },
  selectedIcon: {
    color: theme.palette.primary.light,
  },
  selectable: {
    cursor: 'pointer',
  },
  primaryTextRoot: {
    fontWeight: theme.typography.fontWeightBold,
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}))

type BaseAssetCardProps = {
  isSelected?: boolean
  onSelect?: (event: React.MouseEvent, isSelected: boolean) => void
  onClickDelete?: (event: React.MouseEvent) => void
  onClickRemove?: (event: React.MouseEvent) => void
  onClickDownload?: (event: React.MouseEvent) => void
  onShowPreview?: () => void
  assetsSelected: boolean
  primaryText?: React.ReactNode
  digitalAsset: DigitalAsset
}

const BaseAssetCard: React.FC<React.PropsWithChildren<BaseAssetCardProps>> = (
  props,
) => {
  const { classes, cx } = useStyles()
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null)

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleSelect = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (props.onSelect) {
      props.onSelect(event, !props.isSelected)
    }
  }

  const handleShowPreview = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (props.onShowPreview) {
      props.onShowPreview()
    }
  }

  const handleDownloadClick = (event: React.MouseEvent) => {
    handleClose(event)
    if (props.onClickDownload) {
      props.onClickDownload(event)
    }
  }

  return (
    <Grow in={true} timeout={{ enter: 500 }}>
      <div
        className={cx(classes.root, {
          [classes.selected]: props.isSelected,
          [classes.selectable]: !!props.onSelect,
        })}
        onClick={props.assetsSelected ? handleSelect : handleShowPreview}
      >
        <Media digitalAsset={props.digitalAsset} />
        <div className={classes.content}>
          <Typography className={classes.primaryTextRoot} variant="caption">
            {props.primaryText}
          </Typography>
        </div>
        {props.onSelect && (
          <div
            className={cx(classes.selectHoverRoot, {
              [classes.selectHoverSelected]: props.isSelected,
            })}
          >
            <Fab onClick={handleSelect} size="small">
              {props.isSelected ? (
                <CheckCircleIcon
                  className={classes.selectedIcon}
                  fontSize={'small'}
                />
              ) : (
                <RadioButtonUncheckedIcon fontSize={'small'} />
              )}
            </Fab>
          </div>
        )}
        {props.onClickDownload && (
          <div className={classes.menuHoverRoot}>
            <Fab onClick={handleClick} size="small">
              <MoreVertIcon fontSize="small" />
            </Fab>
          </div>
        )}
        {props.onShowPreview && props.assetsSelected && (
          <div className={classes.previewHoverRoot}>
            <Fab onClick={handleShowPreview} size="small">
              <SearchIcon fontSize="small" />
            </Fab>
          </div>
        )}

        <Menu
          anchorEl={anchorEl}
          keepMounted={true}
          onClose={handleClose}
          open={Boolean(anchorEl)}
        >
          {props.onClickDownload && (
            <MenuItem onClick={handleDownloadClick}>
              <GetAppIcon />
              {I18n.t('downloadables.download')}
            </MenuItem>
          )}
        </Menu>
      </div>
    </Grow>
  )
}

export default BaseAssetCard
